import type { Rng } from "../rng";
import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
	resultFromScores,
} from "../types";

/** The whole vocabulary of the game — nothing is ever typed. */
export const CHARADE_EMOJI = [
	"🌧️",
	"☀️",
	"🌙",
	"⭐",
	"🔥",
	"💧",
	"🌊",
	"🌳",
	"🌸",
	"🍎",
	"🍕",
	"🎂",
	"🐶",
	"🐱",
	"🐟",
	"🐝",
	"🦁",
	"🐘",
	"🚗",
	"✈️",
	"🚀",
	"⚽",
	"🎸",
	"🎨",
	"📚",
	"🏠",
	"👑",
	"⚡",
	"❄️",
	"🎈",
	"🕐",
	"🎁",
] as const;

export const CHARADE_ROUNDS = 6;
export const CHARADE_PICKS = 3;
/** How long the answer stays up before the next round. */
export const CHARADE_REVEAL_MS = 3500;

interface Secret {
	word: string;
	decoys: [string, string, string];
}

const SECRETS: readonly Secret[] = [
	{ word: "Thunderstorm", decoys: ["Beach day", "Snowball fight", "Camping trip"] },
	{ word: "Birthday party", decoys: ["School assembly", "Football match", "Dentist visit"] },
	{ word: "Space rocket", decoys: ["Submarine", "Hot air balloon", "Racing car"] },
	{ word: "Zoo trip", decoys: ["Library visit", "Swimming lesson", "Bike ride"] },
	{ word: "Pizza night", decoys: ["Picnic", "Barbecue", "Breakfast"] },
	{ word: "Rock concert", decoys: ["Art class", "Spelling test", "Sleepover"] },
	{ word: "Treasure hunt", decoys: ["Homework", "Bus journey", "Haircut"] },
	{ word: "Winter morning", decoys: ["Summer evening", "Rainy afternoon", "Windy night"] },
	{ word: "Football final", decoys: ["Chess club", "Cooking show", "Museum tour"] },
	{ word: "Puppy training", decoys: ["Fishing trip", "Kite flying", "Gardening"] },
];

/** One round's worth of secret, dealt in `create` and held until it is played. */
export interface CharadeDeal {
	secret: string;
	options: string[];
	answer: number;
}

export interface CharadesState extends BaseState {
	kind: "charades";
	round: number;
	/** Who is acting this round; the other one guesses. */
	actor: PlayerIndex;
	phase: "acting" | "guessing" | "reveal";
	/** Blanked for the guesser until the reveal. */
	secret: string;
	/** Four options, only shown once the acting is done. */
	options: string[];
	answer: number;
	/**
	 * Every round of the game, dealt up front from the game's own generator.
	 *
	 * Dealing each round as it arrives needs a seed, and the only numbers a pure
	 * `tick` can reach are public ones like the round counter — which makes the
	 * upcoming secret computable by anyone who reads the wire, however the
	 * arithmetic is dressed up. (The first attempt dressed it up badly enough
	 * that five of six rounds were the same secret.) Dealt here, the secrets
	 * come from the unpredictable rng, and `redact` keeps the lot off the wire.
	 */
	deals: CharadeDeal[];
	/** The emoji the actor has chosen so far. */
	clues: number[];
	/** When the reveal ends and the next round is dealt. */
	endsAt: number;
	lastNote: string | null;
}

export type CharadesMove = { clue: number } | { done: true } | { guess: number };

/** All six rounds at once: distinct secrets, each with its own option order. */
function dealAll(rng: Rng): CharadeDeal[] {
	return rng.sample(SECRETS, CHARADE_ROUNDS).map((secret) => {
		const options = rng.shuffle([secret.word, ...secret.decoys]);
		return { secret: secret.word, options, answer: options.indexOf(secret.word) };
	});
}

/** Puts the given round's deal on the table and resets the round bookkeeping. */
function dealRound(state: CharadesState, round: number): CharadesState {
	const deal = state.deals[round];
	return {
		...state,
		phase: "acting",
		secret: deal.secret,
		options: deal.options,
		answer: deal.answer,
		clues: [],
		lastNote: null,
	};
}

export const emojiCharades: GameDefinition<CharadesState, CharadesMove> = {
	id: "charades",
	category: "party",
	title: "Emoji Charades",
	emoji: "🎭",
	blurb: "Act it out with three emoji. Can your friend work out what it is?",
	howTo: [
		"One of you gets a secret thing and picks up to three emoji to describe it.",
		"The other guesses from four options.",
		"A correct guess scores two for the guesser — and the fewer emoji the actor used, the more they score (3, 2 or 1).",
		"Then you swap over.",
	],

	create(rng) {
		return dealRound(
			{
				kind: "charades",
				round: 0,
				actor: 0,
				phase: "acting",
				secret: "",
				options: [],
				answer: 0,
				deals: dealAll(rng),
				clues: [],
				endsAt: 0,
				lastNote: null,
				// Whose go it is depends on the phase, so this is handled below.
				turn: 0,
				scores: [0, 0],
				result: null,
			},
			0,
		);
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const record = raw as Record<string, unknown>;
		if (record.done === true) return { done: true };
		if (typeof record.clue === "number" && Number.isInteger(record.clue)) {
			if (record.clue < 0 || record.clue >= CHARADE_EMOJI.length) return null;
			return { clue: record.clue };
		}
		if (typeof record.guess === "number" && Number.isInteger(record.guess)) {
			if (record.guess < 0 || record.guess > 3) return null;
			return { guess: record.guess };
		}
		return null;
	},

	apply(state, player, move, now): MoveOutcome<CharadesState> {
		if (state.result) return reject("This game is already finished.");

		if ("clue" in move || "done" in move) {
			if (player !== state.actor) return reject("Your friend is acting this one out.");
			if (state.phase !== "acting") return reject("The clues are already in.");

			if ("done" in move) {
				if (state.clues.length === 0) return reject("Pick at least one emoji first.");
				return {
					ok: true,
					state: { ...state, phase: "guessing", turn: otherPlayer(state.actor) },
				};
			}

			const clues = [...state.clues, move.clue];
			// Three emoji is the whole vocabulary — then it goes over automatically.
			return {
				ok: true,
				state:
					clues.length >= CHARADE_PICKS
						? { ...state, clues, phase: "guessing", turn: otherPlayer(state.actor) }
						: { ...state, clues },
			};
		}

		if (player === state.actor) return reject("You know the answer — let them guess!");
		if (state.phase !== "guessing") return reject("Wait for the clues.");

		const right = move.guess === state.answer;
		const scores: [number, number] = [...state.scores];
		if (right) {
			// The guesser earned their two — and the actor is paid for *economy*:
			// three points if one emoji landed it, two for two, one for all
			// three. A flat actor score made most games mirror draws; rewarding
			// the sharper mime lets acting skill actually separate the scores.
			scores[player] += 2;
			scores[state.actor] += Math.max(1, 1 + CHARADE_PICKS - state.clues.length);
		}

		return {
			ok: true,
			state: {
				...state,
				scores,
				phase: "reveal",
				endsAt: now + CHARADE_REVEAL_MS,
				turn: null,
				lastNote: right ? `Yes — ${state.secret}!` : `Not quite — it was ${state.secret}.`,
			},
		};
	},

	legalMoves(state, player) {
		if (state.result) return [];
		if (state.phase === "acting") {
			if (player !== state.actor) return [];
			const moves: CharadesMove[] = CHARADE_EMOJI.map((_, clue) => ({ clue }));
			if (state.clues.length > 0) moves.push({ done: true });
			return moves;
		}
		if (state.phase === "guessing") {
			if (player === state.actor) return [];
			return state.options.map((_, guess) => ({ guess }));
		}
		return [];
	},

	tick(state, now) {
		if (state.result || state.phase !== "reveal" || now < state.endsAt) return null;

		const round = state.round + 1;
		if (round >= CHARADE_ROUNDS) {
			return { ...state, result: resultFromScores(state.scores), turn: null };
		}
		// Swap who acts, and put the next of the pre-dealt secrets on the table.
		const actor = otherPlayer(state.actor);
		return dealRound({ ...state, round, actor, turn: actor }, round);
	},

	nextTickAt(state) {
		// The reveal moves on after a beat; everything else waits for a player.
		return state.result || state.phase !== "reveal" ? null : state.endsAt;
	},

	redact(state, viewer) {
		// The undealt rounds are every secret still to come — including the ones
		// this viewer will be guessing — so nobody receives them. The current
		// round's deal is already copied into the fields below.
		const dealt = { ...state, deals: [] };
		if (state.phase === "reveal" || viewer === state.actor) return dealt;
		// The guesser must not see the answer, and sees the options only when
		// the acting is done.
		return {
			...dealt,
			secret: "",
			answer: -1,
			options: state.phase === "guessing" ? state.options : [],
		};
	},
};
